'use client';

import React from 'react';
import dayjs from 'dayjs';
import { useForm } from 'react-hook-form';
import { css } from 'styled-system/css';

import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@shaojiang/components/ui/button';
import { Collapsible } from '@shaojiang/components/ui/collapsible';
import { Switch } from '@shaojiang/components/ui/switch';
import { toaster } from '@shaojiang/lib/toaster';

import ImagesUploader from './ImagesUploader';
import { type InventoryFormInput, type InventoryFormValues, inventorySchema } from './schema';
import SelectField from './Select';
import TextField from './TextField';

const CATEGORIES = [
  { value: 'computer', label: 'Computer' },
  { value: 'camera', label: 'Camera' },
  { value: 'printer', label: 'Printer' },
  { value: 'server', label: 'Server' },
  { value: 'saas', label: 'SaaS' },
];

const ORDER_TYPES = [
  { value: 'full-paid', label: 'Full Paid' },
  { value: 'partial-paid', label: 'Partial Paid' },
  { value: 'free', label: 'Free' },
  { value: 'prepaid', label: 'Prepaid' },
  { value: 'charity', label: 'Charity' },
];

const DISCOUNT_TYPES = [
  { value: 'promotion', label: 'Promotion' },
  { value: 'large', label: 'Large Scale' },
  { value: 'loyalty', label: 'Loyalty' },
];

const panel = css({
  bg: 'white',
  _dark: { bg: 'rgb(40, 36, 61)' },
  borderRadius: '2xl',
  p: '4',
});
const sectionLabel = css({ color: '#8B8D97', textStyle: 'lg' });
const twoColumns = css({
  display: 'grid',
  gridTemplateColumns: { base: 'repeat(2, minmax(0, 1fr))', smDown: 'repeat(1, minmax(0, 1fr))' },
  columnGap: '6',
});

export default function Main() {
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<InventoryFormInput, unknown, InventoryFormValues>({
    resolver: zodResolver(inventorySchema),
    defaultValues: {
      productName: '',
      category: '',
      sellingPrice: '',
      costPrice: '',
      quantityInStock: '',
      orderType: '',
      addDiscount: false,
      discountType: '',
      discountValue: '',
      addExpiryDate: false,
      expiryDate: dayjs().format('YYYY-MM-DD'),
      expiryTime: '15:30',
      longDescription: '',
    },
  });
  const addDiscount = watch('addDiscount');
  const addExpiryDate = watch('addExpiryDate');

  const onSubmit = (data: InventoryFormValues) => {
    toaster.create({
      type: 'success',
      title: 'Validation passed',
      description: (
        <pre className={css({ textStyle: 'xs', whiteSpace: 'pre-wrap' })}>
          {JSON.stringify(data, null, 2)}
        </pre>
      ),
    });
  };

  const onInvalid = () => {
    toaster.create({ type: 'error', title: 'Please fix the highlighted fields' });
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit, onInvalid)}
      className={css({ w: 'full', minW: '0' })}
      noValidate
    >
      <div
        className={css({
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '4',
          p: '4',
          xsDown: { display: 'block' },
        })}
      >
        <div>
          <h1>Inventory</h1>
          <p>{`Click button "Save & Publish" to trigger validation`}</p>
        </div>
        <div className={css({ display: 'flex', justifyContent: 'flex-end', gap: '2' })}>
          <Button type='button' bg='#142764' color='white'>
            Save as Draft
          </Button>
          <Button type='submit' bg='#0080FF' color='white'>
            Save & Publish
          </Button>
        </div>
      </div>
      <div
        className={css({
          display: 'flex',
          justifyContent: 'space-between',
          gap: '4',
          p: '4',
          pt: '0',
          lgDown: { display: 'block' },
        })}
      >
        <div className={`${panel} ${css({ flex: '1', minW: '0' })}`}>
          <TextField
            label='Product Name'
            placeholder='Product Name'
            error={errors.productName?.message}
            {...register('productName')}
          />
          <SelectField
            label='Product Category'
            options={CATEGORIES}
            error={errors.category?.message}
            {...register('category')}
          />
          <div className={twoColumns}>
            <TextField
              label='Selling Price'
              placeholder='Max 100 please'
              type='number'
              error={errors.sellingPrice?.message}
              {...register('sellingPrice')}
            />
            <TextField
              label='Cost Price'
              placeholder='Min 10 please'
              type='number'
              error={errors.costPrice?.message}
              {...register('costPrice')}
            />
          </div>
          <TextField
            label='Quantity in Stock'
            placeholder='Min 0 please'
            type='number'
            error={errors.quantityInStock?.message}
            {...register('quantityInStock')}
          />
          <SelectField
            label='Order Type'
            options={ORDER_TYPES}
            error={errors.orderType?.message}
            {...register('orderType')}
          />

          <div
            className={css({
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              my: '3',
            })}
          >
            <p className={sectionLabel}>Discount</p>
            <Switch
              checked={!!addDiscount}
              onCheckedChange={({ checked }) => setValue('addDiscount', checked)}
              flexDirection='row-reverse'
            >
              Add Discount
            </Switch>
          </div>
          <Collapsible.Root open={!!addDiscount}>
            <Collapsible.Content>
              <div className={twoColumns}>
                <SelectField
                  label='Discount Type'
                  options={DISCOUNT_TYPES}
                  error={errors.discountType?.message}
                  {...register('discountType')}
                />
                <TextField
                  label='Value'
                  type='number'
                  placeholder='min 0 please'
                  error={errors.discountValue?.message}
                  {...register('discountValue')}
                />
              </div>
            </Collapsible.Content>
          </Collapsible.Root>

          <div
            className={css({
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              my: '3',
            })}
          >
            <p className={sectionLabel}>Expiry Date</p>
            <Switch
              checked={!!addExpiryDate}
              onCheckedChange={({ checked }) => setValue('addExpiryDate', checked)}
              flexDirection='row-reverse'
            >
              Add Expiry Date
            </Switch>
          </div>
          <Collapsible.Root open={!!addExpiryDate}>
            <Collapsible.Content>
              <div className={twoColumns}>
                <TextField
                  label='Date'
                  type='date'
                  error={errors.expiryDate?.message}
                  {...register('expiryDate')}
                />
                <TextField
                  label='Time'
                  type='time'
                  error={errors.expiryTime?.message}
                  {...register('expiryTime')}
                />
              </div>
            </Collapsible.Content>
          </Collapsible.Root>

          <TextField
            label='Product Long Description'
            placeholder='Product Long Description'
            multiline
            rows={10}
            helperText='Add a long description for your product'
            error={errors.longDescription?.message}
            {...register('longDescription')}
          />
        </div>
        <div
          className={`${panel} ${css({
            flexBasis: '320px',
            flexShrink: 0,
            '2xlDown': { flexBasis: '240px', w: '240px' },
            lgDown: { w: 'full', mt: '4' },
          })}`}
        >
          <ImagesUploader />
        </div>
      </div>
    </form>
  );
}
